import { useCallback, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import { Badge } from '@air/primitive-badge';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { WorkspaceAgentMenuItemHoverPrefix } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentMenuItemHoverPrefix';
import {
  WORKSPACE_AGENT_HERO_EDIT_ALL_FIELDS,
  WORKSPACE_AGENT_HERO_RENAME_FILES_BULK,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { useLaunchWorkspaceAgentHeroSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useLaunchWorkspaceAgentHeroSuggestion';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';

const WORKSPACE_AGENT_EDIT_ALL_FIELDS_SEPARATOR: DropdownMenuOption = {
  id: 'workspace-agent-edit-all-fields-separator',
  type: 'separator',
};

const CUSTOM_FIELDS_SUBMENU_IDS = new Set(['bulk-edit-custom-field', 'bulk-edit-custom-fields', 'edit-custom-fields']);

export type EnrichWorkspaceAgentCustomFieldsMenuOptionsParams = {
  clipIds?: Clip['id'][];
};

const insertMenuOptionsBeforeCustomFields = (options: DropdownMenuOption[], toInsert: DropdownMenuOption[]) => {
  if (toInsert.length === 0) {
    return options;
  }

  const customFieldsIndex = options.findIndex((option) => option.id && CUSTOM_FIELDS_SUBMENU_IDS.has(option.id));
  const insertIndex = customFieldsIndex === -1 ? options.length : customFieldsIndex;
  const nextOptions = [...options];
  nextOptions.splice(insertIndex, 0, ...toInsert);

  return nextOptions;
};

const buildWorkspaceAgentSelectionHeroMenuOption = (
  menuOptionKey: 'edit-all-fields-with-tasks' | 'rename-files-in-bulk',
  onSelect: () => void,
): DropdownMenuOption => {
  const menuOption = getDropdownMenuOption(menuOptionKey);

  return {
    ...menuOption,
    className: 'group/dropdown-menu-item',
    onSelect,
    prefix: <WorkspaceAgentMenuItemHoverPrefix icon={menuOption.prefix} />,
    suffix: (
      <Badge appearance="square" className="shrink-0" color="cyan" size="small">
        NEW
      </Badge>
    ),
    type: 'item',
  };
};

export const useWorkspaceAgentSelectionMenuHeroOptions = () => {
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const { isWorkspaceAgentPanelProviderEnabled } = useIsWorkspaceAgentPanelAvailable();
  const { launchHero } = useLaunchWorkspaceAgentHeroSuggestion();

  const isAgentMenuEnabled = isWorkspaceAgentPanelProviderEnabled && !selectAll;

  // @TODO(WORK-714): Hero menu rows still show a temporary cyan NEW suffix; `w-[288px]` on
  // `agentMenuDropdownClassName` / `customFieldsSubmenuClassName` and `useWorkspaceAgentMenuDropdownClassName`
  // is a width hack for those labels. Remove NEW, delete the shared className hook, and drop overrides
  // (or replace with primitive dropdown `size`) once WORK-714 lands.
  const agentMenuDropdownClassName = isAgentMenuEnabled ? 'w-[288px]' : undefined;

  const renameBulkMenuOption = useMemo((): DropdownMenuOption | null => {
    if (!isAgentMenuEnabled) {
      return null;
    }

    return buildWorkspaceAgentSelectionHeroMenuOption('rename-files-in-bulk', () =>
      launchHero(WORKSPACE_AGENT_HERO_RENAME_FILES_BULK.id),
    );
  }, [isAgentMenuEnabled, launchHero]);

  const enrichCustomFieldsMenuOptions = useCallback(
    (
      fieldOptions: DropdownMenuOption[],
      params?: EnrichWorkspaceAgentCustomFieldsMenuOptionsParams,
    ): DropdownMenuOption[] => {
      if (!isAgentMenuEnabled || fieldOptions.length === 0) {
        return fieldOptions;
      }

      const clipIds = params?.clipIds;
      const editAllFieldsMenuOption = buildWorkspaceAgentSelectionHeroMenuOption('edit-all-fields-with-tasks', () => {
        void launchHero(WORKSPACE_AGENT_HERO_EDIT_ALL_FIELDS.id, {
          ...(clipIds?.length ? { clipIds } : {}),
          source: 'selection_menu',
        });
      });

      return [editAllFieldsMenuOption, WORKSPACE_AGENT_EDIT_ALL_FIELDS_SEPARATOR, ...fieldOptions];
    },
    [isAgentMenuEnabled, launchHero],
  );

  const applyMultiClipAgentMenuTransforms = useCallback(
    (
      options: DropdownMenuOption[],
      { hasOnlyClipsSelected, selectedCount }: { hasOnlyClipsSelected: boolean; selectedCount: number },
    ): DropdownMenuOption[] => {
      if (!renameBulkMenuOption || !hasOnlyClipsSelected || selectedCount <= 1) {
        return options;
      }

      return insertMenuOptionsBeforeCustomFields(options, [renameBulkMenuOption]);
    },
    [renameBulkMenuOption],
  );

  const applyAgentSelectionBarEllipsisMenuTransforms = useCallback(
    (
      options: DropdownMenuOption[],
      {
        editTagsOption,
        hasOnlyClipsSelected,
        selectedCount,
      }: {
        editTagsOption: DropdownMenuOption | null;
        hasOnlyClipsSelected: boolean;
        selectedCount: number;
      },
    ): DropdownMenuOption[] => {
      if (!isAgentMenuEnabled || !hasOnlyClipsSelected || selectedCount <= 1) {
        return options;
      }

      let nextOptions = options.filter((option) => option.id !== 'edit-tags');

      if (!nextOptions.some((option) => option.id === 'rename-files-in-bulk') && renameBulkMenuOption) {
        nextOptions = insertMenuOptionsBeforeCustomFields(nextOptions, [renameBulkMenuOption]);
      }

      if (!editTagsOption) {
        return nextOptions;
      }

      const renameIndex = nextOptions.findIndex((option) => option.id === 'rename-files-in-bulk');
      const customFieldsIndex = nextOptions.findIndex(
        (option) => option.id && CUSTOM_FIELDS_SUBMENU_IDS.has(option.id),
      );
      const insertIndex = renameIndex === -1 ? customFieldsIndex : renameIndex;
      const at = insertIndex === -1 ? nextOptions.length : insertIndex;
      const withEditTags = [...nextOptions];
      withEditTags.splice(at, 0, editTagsOption);

      return withEditTags;
    },
    [isAgentMenuEnabled, renameBulkMenuOption],
  );

  return {
    applyAgentSelectionBarEllipsisMenuTransforms,
    applyMultiClipAgentMenuTransforms,
    customFieldsSubmenuClassName: agentMenuDropdownClassName,
    enrichCustomFieldsMenuOptions,
    isAgentMenuEnabled,
    selectionMenuDropdownClassName: agentMenuDropdownClassName,
  };
};
